import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hasCompleteEmergencyContacts, MINIMUM_EMERGENCY_CONTACTS } from '@domain/entities/Person';
import type { Person } from '@domain/entities/Person';
import { formatStoredPhone, phoneDigitsOf } from '@domain/value-objects/PhoneNumber';
import { Banner, Card, Chip } from '@presentation/design-system';
import { useAccess } from '@presentation/hooks/useAccess';
import { getServices } from '@infrastructure/services';
import styles from './EmergencyContactsCard.module.css';

/**
 * A quién llamar si algo pasa mientras esta persona está fuera.
 *
 * **La ficha no los tenía.** El permiso `ver_sos` existía, se encendía en
 * Configuración global y lo honraba una sola pantalla —la de aprobaciones, donde
 * un líder que decide una ausencia ve a quién llamar—. Quien abría la ficha de
 * alguien no los veía nunca, con el interruptor encendido o apagado, y desde
 * fuera eso se lee como un permiso roto.
 *
 * **Sin `ver_sos` la tarjeta no existe**, ni vacía, por lo mismo que la
 * compensación y el expediente: un recuadro que diga «no tienes acceso» confirma
 * que hay unos teléfonos y dónde viven.
 *
 * **Y no pide reidentificarse, a diferencia del expediente y del sueldo.** Es
 * deliberado y va en contra de la simetría: estos números se consultan cuando
 * alguien está fuera y algo urgente ocurre, y una ventana de Google entre quien
 * busca el teléfono y el teléfono es exactamente el segundo que no sobra. Lo que
 * sí se hereda es que sean de sólo lectura aquí — se gestionan en el perfil de su
 * dueño, que es quien los dio.
 */
export const EmergencyContactsCard = ({ person }: { readonly person: Person }): ReactNode => {
  const { holds } = useAccess();
  const canView = holds('ver_sos');

  const contacts = useQuery({
    queryKey: ['personDetail', 'sos', person.id],
    enabled: canView,
    queryFn: async () => {
      const result = await getServices().contacts.listFor(person.id);
      return result.ok ? result.value : [];
    },
  });

  if (!canView) return null;

  const list = contacts.data ?? [];
  const completos = hasCompleteEmergencyContacts(list);

  return (
    <Card title="Contactos de emergencia">
      <div className={styles.body}>
        {contacts.isPending ? (
          <p className={styles.note}>Cargando…</p>
        ) : list.length === 0 ? (
          <Banner tone="warn">
            {person.displayName} no ha registrado ninguno. Se dan de alta desde su propio
            perfil, y nadie más puede hacerlo.
          </Banner>
        ) : (
          <>
            <Chip
              background={completos ? 'var(--color-success-bg)' : 'var(--color-warning-bg)'}
              foreground={completos ? 'var(--color-success-ink)' : 'var(--color-warning-ink)'}
            >
              {completos
                ? 'Completo'
                : `Falta${list.length === MINIMUM_EMERGENCY_CONTACTS - 1 ? '' : 'n'} por registrar`}
            </Chip>

            <ul className={styles.list}>
              {list.map((contact, index) => (
                <li key={contact.id} className={styles.item}>
                  <span className={styles.order}>{index + 1}º</span>
                  <span className={styles.text}>
                    <span className={styles.name}>
                      {contact.name} <span className={styles.relation}>· {contact.relation}</span>
                    </span>
                    {/* El mismo formato que en el perfil y en aprobaciones: el
                        número se guarda en crudo y se agrupa al pintarlo. */}
                    {/*
                      El enlace lleva **dígitos**, lo pintado lleva espacios.
                      Lo guardado no siempre viene limpio —en producción hay
                      números con «+52» y con espacios dentro—, y un `tel:` con
                      espacios no es un `tel:` válido: marca mal o no marca. Es lo
                      único que esta tarjeta existe para hacer.
                    */}
                    <a className={styles.phone} href={`tel:${phoneDigitsOf(contact.phone)}`}>
                      {formatStoredPhone(contact.phone)}
                    </a>
                  </span>
                </li>
              ))}
            </ul>

            {!completos && (
              <p className={styles.note}>
                Con menos de {MINIMUM_EMERGENCY_CONTACTS}, si el primero no contesta no hay a
                quién más llamar. Pídele completarlos en su perfil.
              </p>
            )}
          </>
        )}
      </div>
    </Card>
  );
};
